import React, { useRef } from 'react'
import { Form, Link, useSubmit } from 'react-router'
import { BetaBanner } from '#app/components/beta-banner.tsx'
import { Button } from '#app/components/ui/button.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu.tsx'
import { Icon } from '#app/components/ui/icon'
import { cn } from '#app/lib/utils'
import { ThemeSwitch } from '#app/routes/resources+/theme-switch.tsx'
import { getUserImgSrc } from '#app/utils/misc.tsx'
import { type Theme } from '#app/utils/theme.server'
import { useOptionalUser } from '#app/utils/user.ts'

function UserDropdown() {
	const submit = useSubmit()
	const formRef = useRef<HTMLFormElement>(null)
	const user = useOptionalUser()

	if (user) {
		return (
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button asChild variant="secondary">
						<Link
							to={`/users/${user.username}`}
							onClick={(e) => e.preventDefault()}
							className="flex items-center gap-2"
						>
							<img
								className="h-8 w-8 rounded-full object-cover"
								alt="Profile photograph"
								src={getUserImgSrc(user.image?.id)}
							/>
							<span className="text-body-sm font-bold">
								{user.name ?? user.username}
							</span>
						</Link>
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuPortal>
					<DropdownMenuContent sideOffset={8} align="start" className="z-1k">
						<DropdownMenuItem asChild>
							<Link prefetch="intent" to={`/users/${user.username}`}>
								<Icon className="text-body-md" name="avatar">
									Profile
								</Icon>
							</Link>
						</DropdownMenuItem>
						<DropdownMenuItem asChild>
							<Link prefetch="intent" to={'/users'}>
								<Icon className="text-body-md" name="magnifying-glass">
									Other Users
								</Icon>
							</Link>
						</DropdownMenuItem>
						<DropdownMenuItem asChild>
							<Link
								prefetch="intent"
								to={`/users/${user.username}/new-capture`}
							>
								<Icon className="text-body-md" name="plus">
									Add New Trail
								</Icon>
							</Link>
						</DropdownMenuItem>
						<DropdownMenuItem
							asChild
							onSelect={(event) => {
								event.preventDefault()
								void submit(formRef.current)
							}}
						>
							<Form action="/logout" method="POST" ref={formRef}>
								<Icon className="text-body-md" name="exit">
									<button type="submit">Logout</button>
								</Icon>
							</Form>
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenuPortal>
			</DropdownMenu>
		)
	}

	return (
		<Button asChild variant="default" size="sm">
			<Link to="/login">
				<Icon name="enter" className="sm:hidden">
					<span className="sr-only">Log In</span>
				</Icon>
				<span className="hidden sm:block">Log In</span>
			</Link>
		</Button>
	)
}

export function Header({
	userPreference,
	className = '',
}: {
	className?: string
	userPreference?: Theme | null
}) {
	return (
		<header className={cn('w-full lg:container lg:mx-auto', className)}>
			<nav>
				<div className="flex flex-wrap items-center justify-between gap-2 md:gap-4">
					<Link to="/" className="mr-auto">
						<div className="font-bold">{import.meta.env.VITE_APP_TITLE}</div>
						<div className="hidden font-light md:block">
							{import.meta.env.VITE_APP_DESCRIPTION}
						</div>
					</Link>
					<div>
						<ThemeSwitch userPreference={userPreference} />
					</div>
					<div className="max-w-sm sm:block">
						<Button asChild variant="default" size="sm">
							<Link to="/explore/usa" title="Search Trails">
								<Icon name="magnifying-glass" className="sm:hidden">
									<span className="sr-only">Search Trails</span>
								</Icon>
								<span className="hidden sm:block">Search Trails</span>
							</Link>
						</Button>
					</div>
					<div className="flex gap-10">
						<UserDropdown />
					</div>
				</div>
			</nav>
			<BetaBanner />
		</header>
	)
}
